<template>
  <section id="testimonials" class="section section--alt">
    <div class="test__inner">

      <div class="section-header">
        <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">Testimonials</p>
        <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">
          What Colleagues Say
        </h2>
        <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
          Words from engineers, data professionals, and cross-functional collaborators
          I've had the opportunity to work alongside.
        </p>
      </div>

      <div class="test__grid">
        <div
          v-for="(t, idx) in testimonials"
          :key="t.name"
          :class="['test__card', 'card', 'reveal', `reveal--d${(idx % 4) + 1}`, { 'is-visible': isVisible }]"
        >
          <div class="test__quote-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" fill="rgba(0,212,255,0.12)" stroke="rgba(0,212,255,0.3)" stroke-width="1"/>
              <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" fill="rgba(0,212,255,0.12)" stroke="rgba(0,212,255,0.3)" stroke-width="1"/>
            </svg>
          </div>

          <p class="test__text">{{ t.text }}</p>

          <div class="test__author">
            <div class="test__avatar">{{ t.name[0] }}</div>
            <div class="test__author-info">
              <p class="test__author-name">{{ t.name }}</p>
              <p class="test__author-role">{{ t.role }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()

const testimonials = [
  {
    name: 'Jaydip Barvaliya',
    role: 'Senior Software Engineer at Cognizant',
    text: "Extremely curious person with an unbelievable drive to learn new things and excel in no time. I'd recommend Dixit to any firm — he has demonstrable capabilities that go well beyond his resume. Highly recommend.",
  },
  {
    name: 'Kevin Barvaliya',
    role: 'Data Engineer at Co-operators',
    text: 'Dixit is powerful, knowledgeable, and responsible. He possesses a winning combination of solid tech skills and business sense. He definitely shines in a busy environment. Definitely worth recommending.',
  },
  {
    name: 'Vishal Barvaliya',
    role: 'Data Engineer at TD',
    text: "It's rare that you come across standout talent like Dixit. If you're looking for the best person for analysing data because of his amazing Python, SQL and analytical skills — look no further.",
  },
  {
    name: 'Piyush Vaghela',
    role: 'Biotech with Strong Foundation in Agriculture',
    text: "Dixit is hard-working, dedicated and forward-thinking. We always relied on Dixit in group projects for his problem-solving ability, data analysis and IT skills. If you're looking for a Data Analyst, he's your man!",
  },
  {
    name: 'Sumit Shandilya',
    role: 'Biotechnologist with background in Agriculture',
    text: "We've joined hands on several projects and Dixit is one of the best people I've had as a partner. His ability to tackle any problem is remarkable — Dixit would become an appreciated member of any team.",
  },
  {
    name: 'Nilam Zala',
    role: 'Green Belt Lean Six Sigma Certified Analytics Professional',
    text: 'I highly recommend Dixit and would love to work together again. He is amazing at his job — good with clients, does whatever it takes to help colleagues, and ensures everyone is focused on the main goal.',
  },
  {
    name: 'Siddharth Parmar',
    role: 'Sr. Product Analyst',
    text: 'Very good in SQL and Python. Always a supportive team member and a good talent across teams. He has an impressive knowledge of different IT tools and languages.',
  },
  {
    name: 'Abhishek Rathwa',
    role: 'Customer Support Specialist at Navaera',
    text: "Dixit is good with Product Analysis and has strong SQL knowledge. He's a great person to work with — always ready to help and collaborate.",
  },
]
</script>

<style scoped>
.test__inner {
  max-width: 1200px;
  margin: 0 auto;
}

/* ── Masonry-style 2-column grid ── */
.test__grid {
  columns: 2;
  column-gap: 16px;
}

/* ── Each card ── */
.test__card {
  break-inside: avoid;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 22px;
  margin-bottom: 16px;
  transition: var(--transition);
}
.test__card:hover {
  transform: translateY(-3px);
}

.test__quote-icon { flex-shrink: 0; }

.test__text {
  font-size: 13.5px;
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-style: italic;
  flex: 1;
}

/* ── Author ── */
.test__author {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 14px;
  border-top: 1px solid var(--color-border);
  margin-top: auto;
}

.test__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--color-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: #000;
  flex-shrink: 0;
}

.test__author-info { min-width: 0; }

.test__author-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.test__author-role {
  font-size: 11px;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .test__grid { columns: 1; }
}

@media (max-width: 480px) {
  .test__card { padding: 16px 18px; gap: 10px; }
  .test__text  { font-size: 13px; }
  .test__avatar { width: 34px; height: 34px; font-size: 14px; }
  .test__author-name { font-size: 12px; }
}
</style>
